import { BadgeCheck, Bot, FileText, Image, MapPinned, MessageSquareText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const samples: Array<{ label: string; title: string; body: string; icon: LucideIcon }> = [
  {
    label: 'WebsiteProjectBlock',
    title: 'Website proof block',
    body: 'A tired patio turned into a usable outdoor dining space, with proof points and service links ready for the site.',
    icon: FileText,
  },
  {
    label: 'PortfolioCaseStudy',
    title: 'Portfolio item',
    body: 'A project narrative covering the challenge, work completed, result, related services, and approved images.',
    icon: Image,
  },
  {
    label: 'SocialCaptionShort',
    title: 'Social caption',
    body: 'A concise post that turns the completed work into a human, channel-flexible update.',
    icon: MessageSquareText,
  },
  {
    label: 'GoogleBusinessUpdate',
    title: 'Local update',
    body: 'A location-aware update suitable for manual posting or customer handoff, without claiming direct API publishing.',
    icon: MapPinned,
  },
  {
    label: 'AEOContextSnippet',
    title: 'AI-readable context',
    body: 'Entity, service, location, proof event, claim basis, and buyer questions answered from the same proof job.',
    icon: Bot,
  },
  {
    label: 'ProofPackSummary',
    title: 'Monthly proof pack',
    body: 'A delivery summary showing proof received, outputs generated, held items, AEO notes, and next proof request.',
    icon: BadgeCheck,
  },
];

export function ProofPackSamples() {
  return (
    <section
      id="proof-pack"
      className="border-y border-white/10 bg-obsidian py-24 text-chalk sm:py-28"
      data-aeo-section="proof-pack-samples"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
          <div>
            <p className="text-sm font-black uppercase tracking-wide text-gold">Proof pack samples</p>
            <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight sm:text-5xl">
              One completed job becomes a reusable market asset pack.
            </h2>
            <p className="mt-6 text-lg leading-8 text-ash">
              A fictional garden design example shows the output types. Real customer proof appears
              only after permission and privacy review.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {samples.map((sample) => {
              const Icon = sample.icon;
              return (
                <article key={sample.label} className="border border-white/12 bg-white/[0.03] p-5">
                  <div className="flex items-start gap-4">
                    <span className="grid size-11 shrink-0 place-items-center border border-cyan/30 bg-cyan/10">
                      <Icon className="size-5 text-cyan" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs font-black uppercase tracking-wide text-violet">{sample.label}</p>
                      <h3 className="mt-1 text-lg font-black text-chalk">{sample.title}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-ash">{sample.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
